import { useEffect, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "../axios-orders";
import { queryKeys } from "./queryKeys";
import {
  OfferAssistantConversation,
  OfferAssistantMessage,
  OfferAssistantTarget,
} from "../types";

const BASE_URL = "/api/offer-assistant";

/** How often a pending assistant reply is re-read until the model finishes. */
export const OFFER_ASSISTANT_POLL_MS = 2000;

const NO_MESSAGES: OfferAssistantMessage[] = [];

/** Only the id that is set goes on the wire (`orderId` for single, `multipleOrderId` for joint). */
const targetParams = ({
  orderId,
  multipleOrderId,
}: OfferAssistantTarget): OfferAssistantTarget =>
  orderId ? { orderId } : { multipleOrderId };

export const offerAssistantConversationKey = ({
  orderId,
  multipleOrderId,
}: OfferAssistantTarget) => [
  queryKeys.OFFER_ASSISTANT_CONVERSATION,
  orderId ?? null,
  multipleOrderId ?? null,
];

/** The assistant reply still being computed, if any (at most one per conversation). */
export const findPendingReply = (
  messages: OfferAssistantMessage[]
): OfferAssistantMessage | undefined =>
  messages.find((m) => m.role === "ASSISTANT" && m.status === "PENDING");

const replaceMessage = (
  conversation: OfferAssistantConversation | undefined,
  message: OfferAssistantMessage
): OfferAssistantConversation | undefined =>
  conversation && {
    ...conversation,
    messages: conversation.messages.map((m) =>
      m.id === message.id ? message : m
    ),
  };

/** The user's own turn is not returned by the POST, so it is added locally next to the reply. */
const localUserMessage = (
  text: string,
  reply: OfferAssistantMessage
): OfferAssistantMessage => ({
  id: `local-${reply.id}`,
  role: "USER",
  text,
  status: "DONE",
  recommendation: null,
  createdAt: reply.createdAt,
});

/** i18n key for a failed send: 409 = previous question still pending, 403 = not your order. */
export const sendErrorKey = (error: unknown): string | null => {
  if (!error) return null;
  const status = (error as { response?: { status?: number } })?.response
    ?.status;
  if (status === 409) return "offerAssistant.errors.pending";
  if (status === 403) return "offerAssistant.errors.forbidden";
  return "offerAssistant.errors.send";
};

/**
 * Conversation with the offer assistant for one award (single order or joint order).
 *
 * A turn is asynchronous: the POST answers 202 with the assistant's message in `PENDING`, and this
 * hook polls that message every {@link OFFER_ASSISTANT_POLL_MS} until it is `DONE`/`FAILED`, then
 * writes it back into the cached conversation.
 */
export const useOfferAssistant = (target: OfferAssistantTarget) => {
  const queryClient = useQueryClient();
  const { orderId, multipleOrderId } = target;
  const conversationKey = useMemo(
    () => offerAssistantConversationKey({ orderId, multipleOrderId }),
    [orderId, multipleOrderId]
  );
  const enabled = Boolean(orderId || multipleOrderId);

  const conversation = useQuery({
    queryKey: conversationKey,
    queryFn: async (): Promise<OfferAssistantConversation> => {
      const { data } = await axios.get(`${BASE_URL}/conversation`, {
        params: targetParams(target),
      });
      return data;
    },
    enabled,
  });

  const messages = conversation.data?.messages ?? NO_MESSAGES;
  const pendingReply = findPendingReply(messages);
  const pendingId = pendingReply?.id;

  const poll = useQuery({
    queryKey: [queryKeys.OFFER_ASSISTANT_MESSAGE, pendingId],
    queryFn: async (): Promise<OfferAssistantMessage> => {
      const { data } = await axios.get(`${BASE_URL}/messages/${pendingId}`);
      return data;
    },
    enabled: Boolean(pendingId),
    refetchInterval: (query) =>
      query.state.data && query.state.data.status !== "PENDING"
        ? false
        : OFFER_ASSISTANT_POLL_MS,
  });

  const polled = poll.data;
  useEffect(() => {
    if (!polled || polled.status === "PENDING" || polled.id !== pendingId) {
      return;
    }
    queryClient.setQueryData<OfferAssistantConversation>(
      conversationKey,
      (old) => replaceMessage(old, polled)
    );
  }, [polled, pendingId, queryClient, conversationKey]);

  const send = useMutation({
    mutationFn: async (text: string): Promise<OfferAssistantMessage> => {
      const { data } = await axios.post(`${BASE_URL}/messages`, {
        ...targetParams(target),
        text,
      });
      return data;
    },
    onSuccess: (reply, text) => {
      queryClient.setQueryData<OfferAssistantConversation>(
        conversationKey,
        (old) => ({
          id: old?.id ?? null,
          messages: [
            ...(old?.messages ?? []),
            localUserMessage(text, reply),
            reply,
          ],
        })
      );
    },
    onError: (error) => {
      // Someone (another tab) already has a question pending: reload so its reply shows up.
      if (sendErrorKey(error) === "offerAssistant.errors.pending") {
        queryClient.invalidateQueries({ queryKey: conversationKey });
      }
    },
  });

  return {
    messages,
    isLoading: conversation.isLoading,
    isLoaded: conversation.isSuccess,
    isLoadError: conversation.isError,
    /** A question is being sent or its answer is still pending — the input must wait. */
    isBusy: send.isPending || Boolean(pendingReply),
    /** `onSent` runs once the question is accepted (e.g. to clear the input). */
    send: (text: string, onSent?: () => void) =>
      send.mutate(text, { onSuccess: () => onSent?.() }),
    sendErrorKey: sendErrorKey(send.error),
    clearSendError: send.reset,
  };
};
